import React, { Component } from 'react';
import PropTypes from 'prop-types';
import onClickOutside from 'react-onclickoutside';
import moment from 'moment';
import axios from '../lib/axios';
import Avatar from './Avatar';
import qs from 'qs';

export class SearchBar extends Component {
  static propTypes = {
    searchUrl: PropTypes.string.isRequired,
    query: PropTypes.string,
    currentCommunity: PropTypes.shape({
      name: PropTypes.string.isRequired,
    }).isRequired,
    requestThrottle: PropTypes.number,
    isSearchPage: PropTypes.bool,
  };

  static defaultProps = {
    query: null,
    recentUsersProfiles: [],
    recentCompaniesProfiles: [],
    recentSearches: [],
    recommendedSearches: [],
    requestThrottle: 250,
    isSearchPage: false,
  };

  constructor(props) {
    super(props);

    this.state = {
      query: props.query,
      results: [],
      focused: props.isSearchPage,
      loading: false,
    };

    this.request = null;
    this.requestCancelToken = axios.CancelToken.source();
    this.searchInputRef = React.createRef();
  }

  handleChange = (e) => {
    const query = e.target.value;
    const url = this.props.searchUrl;

    if (this.request) {
      this.requestCancelToken.cancel();
    }

    if (!query) {
      return this.setState({ results: [], query: null });
    }
    if (!this.props.isSearchPage) {
      this.setState({ query, loading: true }, () => {
        clearTimeout(this.requestThrottle);
        this.requestThrottle = setTimeout(() => {
          this.performRequest(url, { query, previewsOnly: true });
        }, this.props.requestThrottle);
      });
    }
  };

  performRequest = (url, params) => {
    this.requestCancelToken = axios.CancelToken.source();

    this.request = axios.get(url, {
      params,
      paramsSerializer: (p) => qs.stringify(p, { arrayFormat: 'brackets', depth: 2 }),
      cancelToken: this.requestCancelToken.token,
    });

    this.request.then(
      (response) => {
        const { data } = response;
        this.setState({ results: data, loading: false });
      },
      (error) => {
        console.error(error);
      }
    );
  };

  handleMobileFocusClick = () => {
    this.setState({ focused: true }, () => {
      this.searchInputRef.current.focus();
    });
  };

  handleFocus = () => {
    this.setState({ focused: true });
  };

  handleClickOutside = () => {
    if (this.state.focused) {
      this.setState({ focused: false });
    }
  };

  renderAdvancedSearchBlock(label) {
    return (
      <div className="u-py-3 u-border-t u-flex u-justify-center u-flex-shrink-0 u-fixed lg:u-relative u-inset-x-0 u-z-10 u-bg-white u-bottom-0">
        <a href={`${this.props.searchUrl}?query=${this.state.query}`}>
          {' '}
          {label} "{this.state.query}"
        </a>
      </div>
    );
  }

  renderResultLoading = (n) => {
    const repetitions = [];
    for (let step = 0; step < (n || 1); step++) {
      repetitions.push(
        <div key={`loading-${step}`} className="u-flex u-items-center u-p-2">
          <div className="shimmer u-rounded-full u-w-11 u-h-11 u-mr-2" />
          <div className="u-flex u-flex-1 u-flex-col">
            <div className="shimmer u-rounded u-h-3 u-w-2/5 u-mb-1" />
            <div className="shimmer u-rounded u-h-3 u-w-2/3 u-mb-1" />
          </div>
        </div>
      );
    }
    return <div className="u-px-2 lg:u-px-0">{repetitions}</div>;
  };

  renderResultThumbnail(result) {
    return (
      <div>
        {['Event', 'OfficeHour', 'Checkin'].includes(result.type) && (
          <div className="u-flex-none u-mr-4">
            <div className="u-rounded u-border-blue u-border u-py-1 u-text-center u-flex u-flex-col u-items-center u-justify-center u-w-10">
              <span className="u-text-xl u-leading-none u-text-black">
                {moment(result.start_date).format('DD')}
              </span>
              <span className="u-uppercase u-text-xs u-text-black">
                {moment(result.start_date).format('MMM')}
              </span>
            </div>
          </div>
        )}
        {!['Event', 'OfficeHour', 'Checkin'].includes(result.type) &&
          result.avatar !== undefined && (
            <Avatar
              src={result.avatar.image}
              name={result.avatar.name}
              type={result.avatar.type}
              className="Card-avatar"
            />
          )}
      </div>
    );
  }

  renderResults() {
    const results = this.state.results.map((result) => (
      <a
        className="u-flex u-items-center hover:u-bg-grey-lighter u-p-2"
        key={result.id}
        href={result.url}>
        {this.renderResultThumbnail(result)}
        <div>
          <div>{result.title}</div>
          <div className="u-text-sm u-text-grey">{result.subtitle}</div>
        </div>
      </a>
    ));

    return (
      <div className="u-pb-12 lg:u-pb-0">
        <div className="u-px-2 lg:u-px-0">{results}</div>
        {this.renderAdvancedSearchBlock('See all results for')}
      </div>
    );
  }

  renderNoResults() {
    return (
      <div className="u-pb-12 lg:u-pb-0">
        <div className="u-px-2 lg:u-px-0">
          <span className="u-p-2 u-block">No results found</span>
        </div>
        {this.renderAdvancedSearchBlock('Try advanced search for')}
      </div>
    );
  }

  renderSearchBar() {
    return (
      <div className={`search-bar-container ${this.state.focused ? 'is-focused' : ''}`}>
        <form action={this.props.searchUrl}>
          <div className="search-bar">
            <input
              type="search"
              name="query"
              placeholder={`Search ${this.props.currentCommunity.name}`}
              className="search-bar-input"
              onFocus={this.handleFocus}
              onChange={this.handleChange}
              defaultValue={this.state.query}
              autoComplete="off"
              ref={this.searchInputRef}
            />
            <button type="submit" className="search-bar-btn">
              <i className="Icon Icon-search" />
            </button>
          </div>
        </form>
        <i
          className="Icon Icon-close u-text-lg u-font-semibold u-flex u-ml-4 lg:u-hidden"
          onClick={this.handleClickOutside}
        />
      </div>
    );
  }

  renderResultsList() {
    return (
      <div className="search-bar-results-list">
        {this.state.loading && this.renderResultLoading(3)}
        {this.state.results.length > 0 && !this.state.loading && this.renderResults()}
        {this.state.results.length === 0 && !this.state.loading && this.renderNoResults()}
      </div>
    );
  }

  render() {
    return (
      <div className="search-bar-wrapper">
        <div className="u-relative u-w-full u-h-full u-flex u-items-center">
          {this.renderSearchBar()}
          <i
            className="Icon Icon-search u-text-xl u-flex u-mr-4 lg:u-hidden"
            onClick={this.handleMobileFocusClick}
          />
          {this.state.focused &&
            this.state.query &&
            !this.props.isSearchPage &&
            this.renderResultsList()}
        </div>
      </div>
    );
  }
}

export default onClickOutside(SearchBar);
